import { Button } from '@/ui-kit'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { clsx } from 'clsx'
import { WalletMinimal } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type ConnectWalletButtonProps = {
  className?: string
}

export const ConnectWalletButton = ({ className }: ConnectWalletButtonProps) => {
  const { publicKey, disconnect, connecting, wallet } = useWallet()
  const { setVisible } = useWalletModal()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const truncatedAddress = useMemo(() => {
    if (!publicKey) return null
    const address = publicKey.toBase58()
    return `${address.slice(0, 4)}...${address.slice(-4)}`
  }, [publicKey])

  const address = publicKey?.toBase58() ?? ''

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  const handleCopyAddress = useCallback(async () => {
    if (!address) return
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }, [address])

  const handleDisconnect = useCallback(() => {
    setDropdownOpen(false)
    disconnect()
  }, [disconnect])

  const handleConnectClick = useCallback(() => {
    setVisible(true)
  }, [setVisible])

  if (!publicKey) {
    return (
      <Button
        type="button"
        size="small"
        variant="bordered-gray"
        onClick={handleConnectClick}
        isDisabled={connecting}
        className={clsx(connecting && 'cursor-wait opacity-70', className, 'gap-2')}
      >
        <WalletMinimal size={18} />
        {connecting ? (
          <>
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Connecting...
          </>
        ) : (
          'Connect Wallet'
        )}
      </Button>
    )
  }

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      <Button
        type="button"
        size="small"
        variant="bordered-gray"
        onClick={() => setDropdownOpen((prev) => !prev)}
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
        className={clsx('gap-2 w-full')}
      >
        <img className="h-5 w-5" src={wallet?.adapter.icon} alt={wallet?.adapter.name} />
        {truncatedAddress}
      </Button>
      {dropdownOpen && (
        <div
          className="border-gray-70 absolute top-full right-0 z-10 mt-2 min-w-[145px] rounded-lg border bg-black py-1"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleCopyAddress}
            className="text-gray-80 flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm transition-colors first:rounded-t-[11px] hover:bg-white/10"
          >
            {copied ? 'Copied!' : 'Copy address'}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleDisconnect}
            className="text-gray-80 flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm transition-colors last:rounded-b-[11px] hover:bg-white/10"
          >
            Disconnect
          </button>
        </div>
      )}
    </div>
  )
}
